import type { ActionProp } from './types'

/**
 * Resolve an ActionProp to an href string
 *
 * @param action - The action prop to resolve
 * @returns The href string, or undefined if action is a function
 *
 * @example
 * resolveAction('/about') // '/about'
 * resolveAction({ href: '/about', target: '_blank' }) // '/about'
 * resolveAction(() => console.log('clicked')) // undefined
 */
export function resolveAction(action: ActionProp | undefined): string | undefined {
  if (!action) return undefined
  if (typeof action === 'string') return action
  if (typeof action === 'function') return undefined
  return action.href
}

/**
 * Resolve an ActionProp to link props (href + target)
 *
 * @param action - The action prop to resolve
 * @returns Object with href and optional target, or undefined
 *
 * @example
 * resolveActionProps({ href: '/about', target: '_blank' })
 * // { href: '/about', target: '_blank' }
 */
export function resolveActionProps(
  action: ActionProp | undefined
): { href: string; target?: '_blank' | '_self' } | undefined {
  if (!action) return undefined
  if (typeof action === 'string') return { href: action }
  if (typeof action === 'function') return undefined
  return { href: action.href, target: action.target }
}

/**
 * Check if an action is a click handler (function)
 */
export function isClickAction(action: ActionProp | undefined): action is () => void {
  return typeof action === 'function'
}

/**
 * Get onClick handler from an ActionProp
 */
export function getOnClick(action: ActionProp | undefined): (() => void) | undefined {
  if (!action) return undefined
  if (typeof action === 'function') return action
  if (typeof action === 'object' && action.onClick) return action.onClick
  return undefined
}
